import { Flame, Lightbulb, Droplets, Wrench, type LucideIcon } from "lucide-react";

/**
 * Single source of truth for the icon + color tone we use whenever the
 * UI needs to draw an asset chip, list row, or table cell. Centralizing
 * this keeps backflow blue, extinguishers red, and emergency lights
 * yellow consistent across:
 *
 *   - Field job detail device card
 *   - Field backflow workflow DeviceCard parent badge
 *   - Field fire-safety workflow device list rows
 *   - Office Devices table Asset column
 *   - Office "Add device" dialog rows
 *
 * Tone classes are intentionally Tailwind utilities (not custom theme
 * tokens) so dark-mode contrast stays in sync with the rest of the app
 * without anyone having to remember to keep two configs aligned.
 */
export type AssetTone = "fs-red" | "fs-yellow" | "bf-blue" | "neutral";

export interface AssetVisual {
    Icon: LucideIcon;
    /** Bg+foreground utilities suitable for a small badge / pill. */
    badgeClassName: string;
    /** Plain foreground utility for an inline lucide icon. */
    iconClassName: string;
    tone: AssetTone;
    /** Short, human label ("Extinguisher", "Emergency Light", "Backflow"). */
    label: string;
}

const VISUAL_BY_TYPE: Record<string, AssetVisual> = {
    extinguisher: {
        Icon: Flame,
        tone: "fs-red",
        badgeClassName: "bg-red-500/15 text-red-600 dark:text-red-300",
        iconClassName: "text-red-600 dark:text-red-400",
        label: "Extinguisher",
    },
    e_light: {
        Icon: Lightbulb,
        tone: "fs-yellow",
        badgeClassName: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
        iconClassName: "text-amber-600 dark:text-amber-400",
        label: "Emergency Light",
    },
    backflow: {
        Icon: Droplets,
        tone: "bf-blue",
        badgeClassName: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
        iconClassName: "text-sky-600 dark:text-sky-400",
        label: "Backflow",
    },
};

const NEUTRAL_VISUAL: AssetVisual = {
    Icon: Wrench,
    tone: "neutral",
    badgeClassName: "bg-muted text-foreground/80",
    iconClassName: "text-foreground/70",
    label: "Device",
};

/** Resolve the visual treatment for an asset given its `asset_type`
 *  (the canonical column set in 087_assets_asset_type_taxonomy.sql).
 *  Unknown / NULL types fall back to a neutral wrench so legacy rows
 *  still render rather than crashing the table. */
export function assetVisual(assetType: string | null | undefined): AssetVisual {
    if (!assetType) return NEUTRAL_VISUAL;
    return VISUAL_BY_TYPE[assetType] ?? NEUTRAL_VISUAL;
}
